import { useRef, useState, useMemo, useEffect } from "react";
import { Plus, RefreshCcw, Eye, EyeOff, Sparkles, Loader2 } from "lucide-react";
import { Button } from "@oc-labs/wt-storybook";
import toast from "react-hot-toast";
import useTranslationAdmin from "../../../hooks/useTranslationAdmin";
import ConfirmModal from "../../../components/modals/ConfirmModal";
import UpsertLanguageModal from "../../../components/modals/translation/UpsertLanguageModal";
import UpsertTermModal from "../../../components/modals/translation/UpsertTermModal";
import UpsertTranslationModal from "../../../components/modals/translation/UpsertTranslationModal";
import ImportTranslationModal from "../../../components/modals/translation/ImportTranslationModal";
import useModal from "../../../hooks/useModal";

const defaultProjects = [
  { id: "a3d87b0c-e46b-48b7-9ea4-fa522cacecd0", name: "Wallettwo" },
  { id: "4e98d028-5346-41fc-b38e-d9a8852c2f04", name: "Engage" },
];

export default function AdminTranslations() {
  const translationAdmin = useTranslationAdmin(defaultProjects);

  const {
    translations,
    selectProject,
    languages,
    selectedProject,
    deleteLanguage,
    terms,
    deleteTerm,
    translateTerm,
    refreshTranslations,
  } = translationAdmin;

  const { open: openConfirm } = useModal("confirm-modal");
  const { open: openUpsertLanguage } = useModal("translation-upsert-language");
  const { open: openUpsertTerm } = useModal("translation-upsert-term");
  const { open: openUpsertTranslation } = useModal("translation-upsert-translation");

  const [search, setSearch] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [selectedLanguages, setSelectedLanguages] = useState(["en"]);
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);
  const [translatingCell, setTranslatingCell] = useState(null);
  const [translatingLang, setTranslatingLang] = useState(null);

  const handleTranslateAll = async (language) => {
    if (language.code === "en") return;

    const missing = terms.filter((term) => {
      const termKey = term.code ?? term.term ?? term.id;
      return !translationMap.get(`${termKey}-${language.code}`);
    });

    if (missing.length === 0) {
      toast.success(`All terms already translated for ${language.name}`);
      return;
    }

    setTranslatingLang(language.code);
    const toastId = toast.loading(`Translating ${language.name}: 0/${missing.length}`);

    try {
      for (let i = 0; i < missing.length; i++) {
        const term = missing[i];
        const termKey = term.code ?? term.term ?? term.id;
        toast.loading(`Translating ${language.name}: ${i + 1}/${missing.length}`, { id: toastId });
        await translateTerm({
          termId: term.id,
          termCode: termKey,
          languageId: language.id,
          languageCode: language.code,
          silent: true,
        });
      }
      toast.success(`Translated ${missing.length} terms for ${language.name}`, { id: toastId });
    } catch {
      toast.error(`Failed after translating some terms for ${language.name}`, { id: toastId });
    } finally {
      setTranslatingLang(null);
    }
  };
  const [showDescription, setShowDescription] = useState(true);
  const searchRef = useRef(null);
  const searchWrapperRef = useRef(null);
  const languageMenuRef = useRef(null);

  const handleDeleteLanguage = async (language) => {
    openConfirm({
      title: "Delete Language",
      description: `Are you sure you want to delete ${language.name}?`,
      onConfirm: () => deleteLanguage(language.code),
    });
  };

  const handleDeleteTerm = async (term) => {
    openConfirm({
      title: "Delete Term",
      description: `Are you sure you want to delete ${term.term}?`,
      onConfirm: () => deleteTerm(term),
    });
  };

  const handleRefresh = async () => {
    await refreshTranslations();
  };

  const handleSearchVisible = () => {
    setShowSearch((prev) => {
      const next = !prev;
      if (next && searchRef.current) {
        searchRef.current.focus();
      }
      return next;
    });
  };

  const filteredTerms = useMemo(() => {
    const lowerSearch = search.toLowerCase();
    return terms
      .filter((term) => term.term.toLowerCase().includes(lowerSearch))
      .sort((a, b) => a.term.localeCompare(b.term));
  }, [terms, search]);

  const translationMap = useMemo(() => {
    const map = new Map();
    translations.forEach((t) => {
      const termKey = t.termCode ?? t.termId ?? "";
      const languageKey = t.languageCode ?? t.languageId ?? "";
      map.set(`${termKey}-${languageKey}`, t.translation);
    });
    return map;
  }, [translations]);

  // Languages to display in the table (filtered or all)
  const displayedLanguages = useMemo(() => {
    if (selectedLanguages.length === 0) return languages;
    return languages.filter((lang) => selectedLanguages.includes(lang.code));
  }, [languages, selectedLanguages]);

  const toggleLanguageFilter = (languageCode) => {
    setSelectedLanguages((prev) => {
      if (prev.includes(languageCode)) {
        return prev.filter((code) => code !== languageCode);
      } else {
        return [...prev, languageCode];
      }
    });
  };

  const showAllLanguages = () => {
    setSelectedLanguages([]);
  };

  const getLanguageButtonText = () => {
    if (selectedLanguages.length === 0) return "All Languages";
    if (selectedLanguages.length === 1) {
      const lang = languages.find((l) => l.code === selectedLanguages[0]);
      return lang?.name || "1 Language";
    }
    return `${selectedLanguages.length} Languages`;
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (languageMenuRef.current && !languageMenuRef.current.contains(event.target)) {
        setShowLanguageMenu(false);
      }
    };

    if (showLanguageMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showLanguageMenu]);

  return (
    <div className="2xl:p-6 p-3">
      <UpsertLanguageModal translationAdmin={translationAdmin} />
      <ConfirmModal />
      <UpsertTermModal translationAdmin={translationAdmin} />
      <UpsertTranslationModal translationAdmin={translationAdmin} />
      <ImportTranslationModal />

      {/* Project Selector */}
      <div className="py-2">
        <div className="flex flex-between items-center">
          <div className="flex flex-wrap gap-2">
            {defaultProjects.map((project) => (
              <div key={project.id}>
                <Button
                  type={
                    selectedProject?.id === project.id
                      ? "primary"
                      : "tertiary"
                  }
                  size="small"
                  onClick={() => selectProject(project)}
                >
                  {project.name}
                </Button>
              </div>
            ))}
          </div>

          <div className="grow"></div>

          <div className="flex items-center gap-2">
            {/* Show Description Button when hidden */}
            {!showDescription && (
              <Button
                type="tertiary"
                size="small"
                className="whitespace-nowrap"
                icon={<EyeOff size={16} />}
                onClick={() => setShowDescription(true)}
                title="Show description column"
              >
                Show Description
              </Button>
            )}

            {/* Language Filter Dropdown with Checkboxes */}
            <div className="relative" ref={languageMenuRef}>
              <button
                className="h-8 px-3 pr-8 bg-gray-100 rounded-2xl text-sm hover:bg-gray-200 transition-colors flex items-center gap-2"
                onClick={() => setShowLanguageMenu(!showLanguageMenu)}
              >
                <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                <span className="whitespace-nowrap">{getLanguageButtonText()}</span>
                <svg className={`w-4 h-4 text-gray-600 transition-transform  ${showLanguageMenu ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {showLanguageMenu && (
                <div className="absolute top-full mt-1 left-0 bg-white border border-gray-300 rounded-lg shadow-lg z-50 min-w-[200px]">
                  <div className="p-2">
                    {/* All Languages Option */}
                    <label className="flex items-center px-3 py-2 hover:bg-gray-50 rounded cursor-pointer whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedLanguages.length === 0}
                        onChange={showAllLanguages}
                        className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500 whitespace-nowrap"
                      />
                      <span className="ml-2 text-sm font-medium whitespace-nowrap">All Languages</span>
                    </label>
                    
                    <div className="border-t border-gray-200 my-1"></div>
                    
                    {/* Individual Languages */}
                    {languages.map((language) => (
                      <label
                        key={language.code}
                        className="flex items-center px-3 py-2 hover:bg-gray-50 rounded cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedLanguages.includes(language.code)}
                          onChange={() => toggleLanguageFilter(language.code)}
                          className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                        />
                        <span className="ml-2 text-sm">{language.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Button
              type="tertiary"
              size="small"
              className="whitespace-nowrap"
              icon={<RefreshCcw size={16} />}
              onClick={handleRefresh}
            >
              Refresh
            </Button>

            <Button
              type="secondary"
              size="small"
              className="whitespace-nowrap"
              icon={<Plus size={16} />}
              onClick={() =>
                openUpsertTerm({
                  projectId: selectedProject?.id,
                })
              }
            >
              Create Term
            </Button>

            <Button
              type="secondary"
              size="small"
              className="whitespace-nowrap"
              icon={<Plus size={16} />}
              onClick={() =>
                openUpsertLanguage({
                  projectId: selectedProject?.id,
                })
              }
            >
              Create Language
            </Button>

            {/* Search */}
            <div className="relative" ref={searchWrapperRef}>
              <input
                ref={searchRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search terms..."
                onFocus={() => setShowSearch(true)}
                className={`pl-10 pr-4 py-1.5 h-8 bg-gray-100 rounded-full text-sm ${
                  showSearch ? "w-80" : "w-40"
                } outline-none transition-all duration-300`}
              />
              <img
                src="/icons/search.svg"
                alt="Search"
                className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 cursor-pointer"
                onClick={handleSearchVisible}
              />

              {search && (
                <img
                  src="/icons/clear_input.svg"
                  alt="Clear"
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 cursor-pointer opacity-60 hover:opacity-100"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSearch("");
                    setShowSearch(true);
                    searchRef.current?.focus();
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </div>


      {/* Table */}
      <div className="overflow-x-auto rounded-lg shadow">

        <table className="min-w-full bg-white text-sm">
          <thead className="bg-gray-100 uppercase text-sm tracking-wider">
            <tr>
              <th className="py-3 px-6 text-left font-medium w-64">Term</th>
              {showDescription && (
                <th className="py-3 px-6 text-left font-medium w-75">
                  <div className="flex items-center gap-2">
                    Description
                    <button
                      onClick={() => setShowDescription(false)}
                      className="text-gray-500 hover:text-gray-700"
                      title="Hide description"
                    >
                      <Eye size={16} />
                    </button>
                  </div>
                </th>
              )}
              {displayedLanguages.map((language) => (
                <th
                  key={language.code}
                  className="py-3 px-6 text-left font-medium"
                >
                  <div className="flex items-center gap-1">
                    {language.name}
                    {language.code !== "en" && (
                      <button
                        onClick={() => handleTranslateAll(language)}
                        disabled={translatingLang === language.code}
                        className={`p-1 rounded transition ${
                          translatingLang === language.code
                            ? "text-amber-500 animate-pulse"
                            : "text-gray-400 hover:text-amber-500 hover:bg-amber-50"
                        }`}
                        title="Translate all missing"
                      >
                        {translatingLang === language.code ? (
                          <Loader2 size={14} className="animate-spin" />
                        ) : (
                          <Sparkles size={14} />
                        )}
                      </button>
                    )}
                    <button
                      onClick={() => handleDeleteLanguage(language)}
                      className="text-red-600 hover:text-red-900 font-bold"
                    >
                      X
                    </button>
                  </div>
                </th>
              ))}
              <th className="py-3 px-6 w-25"></th>
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-200 text-sm">
            {filteredTerms.map((term) => (
              <tr key={term.id} className="hover:bg-gray-50 transition">
                <td className="py-4 px-6 font-medium text-gray-900 w-64 max-w-64 truncate">
                  {term.term}
                </td>

                {showDescription && (
                  <td className="py-4 px-6 max-w-62.5 truncate">
                    <button
                      className={`${
                        term?.description
                          ? "text-indigo-600 hover:text-indigo-900"
                          : "text-gray-600 hover:text-gray-900"
                      } w-full text-left truncate cursor-pointer`}
                      onClick={() =>
                        openUpsertTerm({
                          ...term,
                          projectId: selectedProject?.id,
                        })
                      }
                    >
                      {term?.description || "MISSING"}
                    </button>
                  </td>
                )}

                {displayedLanguages.map((language) => {
                  const termKey = term.code ?? term.term ?? term.id;
                  const languageKey = language.code ?? language.id;
                  const translation = translationMap.get(
                    `${termKey}-${languageKey}`
                  );

                  const cellKey = `${termKey}-${languageKey}`;
                  const isTranslating = translatingCell === cellKey;

                  const handleTranslate = async (e) => {
                    e.stopPropagation();
                    setTranslatingCell(cellKey);
                    await translateTerm({
                      termId: term.id,
                      termCode: term.code ?? term.term,
                      languageId: language.id,
                      languageCode: language.code,
                    });
                    setTranslatingCell(null);
                  };

                  return (
                    <td
                      key={language.code}
                      className="py-4 px-6 max-w-62.5"
                    >
                      <div className="flex items-center gap-1.5 group/cell">
                        <button
                          className={`${
                            translation
                              ? "text-indigo-600 hover:text-indigo-900"
                              : "text-gray-600 hover:text-gray-900"
                          } text-left truncate cursor-pointer flex-1 min-w-0`}
                          onClick={() =>
                            openUpsertTranslation({
                              term,
                              language,
                              translation,
                            })
                          }
                        >
                          {translation || "MISSING"}
                        </button>
                        {languageKey !== "en" && (
                          <button
                            onClick={handleTranslate}
                            disabled={isTranslating}
                            className={`shrink-0 p-1 rounded transition ${
                              isTranslating
                                ? "text-amber-500 animate-pulse"
                                : "text-gray-400 hover:text-amber-500 hover:bg-amber-50"
                            }`}
                            title="Auto-translate"
                          >
                            <Sparkles size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                  );
                })}

                <td className="py-4 px-6 whitespace-nowrap">
                  <Button
                    type="danger"
                    size="small"
                    onClick={() => handleDeleteTerm(term)}
                  >
                    Delete
                  </Button>
                </td>
              </tr>
            ))}

            {filteredTerms.length === 0 && (
              <tr>
                <td
                  colSpan={(showDescription ? 2 : 1) + displayedLanguages.length + 1}
                  className="py-4 px-6 text-center text-gray-500 italic text-sm"
                >
                  No results found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
